import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IV_LANDING } from "./innerVoiceLandingContent";
import { SUPPORT_EMAIL } from "../legal/legalContent";
import { IvIcon } from "./InnerVoiceLandingIcons";
import ReadAloudBar from "../components/ReadAloudBar";
import { collectUiStringsForTts } from "../utils/collectUiStringsForTts";
import "./InnerVoiceLanding.css";

/** לוגו בלי קובץ רסטר — SVG + טקסט, רקע שקוף; צבע מ־`--ivlp-gold` (בלי רקע כחול “אפוי” בתמונה). */
function InnerVoiceLogoLockup({ showWordmark = true, className = "" }) {
  return (
    <div
      className={`ivlp-logo-lockup ${showWordmark ? "ivlp-logo-lockup--with-type" : "ivlp-logo-lockup--glyph-only"} ${className}`.trim()}
      dir="ltr"
      aria-label="InnerVoice"
    >
      <svg className="ivlp-logo-glyph" viewBox="0 0 48 48" role="img" aria-hidden="true">
        <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="2.25" />
        <path
          d="M 13 26 Q 19 20 24 26 Q 29 32 35 26"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {showWordmark ? <span className="ivlp-logo-type">InnerVoice</span> : null}
    </div>
  );
}

function detectLang() {
  const qs = new URLSearchParams(window.location.search);
  const urlLang = qs.get("lang");
  if (urlLang && IV_LANDING[urlLang]) return urlLang;

  const saved = window.localStorage.getItem("innervoice_landing_lang");
  if (saved && IV_LANDING[saved]) return saved;

  const appLang = window.localStorage.getItem("innervoice_lang");
  if (appLang && IV_LANDING[appLang]) return appLang;

  const b = (navigator.language || "en").toLowerCase();
  if (b.startsWith("he")) return "he";
  if (b.startsWith("es")) return "es";
  return "en";
}

function journeyHref(lang) {
  return `/journey?lang=${encodeURIComponent(lang)}`;
}

function withLang(path, lang) {
  return path.includes("?") ? `${path}&lang=${encodeURIComponent(lang)}` : `${path}?lang=${encodeURIComponent(lang)}`;
}

function useReveal() {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setOn(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, on];
}

function IvReveal({ children, className = "" }) {
  const [ref, on] = useReveal();
  return (
    <div ref={ref} className={`ivlp-reveal ${on ? "ivlp-reveal--on" : ""} ${className}`.trim()}>
      {children}
    </div>
  );
}

function SectionHead({ icon, title, lead }) {
  return (
    <div className="ivlp-sec-head">
      {icon ? (
        <div className="ivlp-sec-icon" aria-hidden="true">
          <IvIcon name={icon} />
        </div>
      ) : null}
      <h2>{title}</h2>
      {lead ? <p className="ivlp-sec-lead">{lead}</p> : null}
    </div>
  );
}

/** Canonical language-specific preview screenshots — never cross-map languages. */
const PREVIEW_SCREEN_KEYS = ["pick", "day", "write", "reflect"];

const PREVIEW_SCREENSHOTS = {
  he: {
    pick: "/innervoice-screens/he-pick.png",
    day: "/innervoice-screens/he-day.png",
    write: "/innervoice-screens/he-write.png",
    reflect: "/innervoice-screens/he-reflect.png",
  },
  en: {
    pick: "/innervoice-screens/en-pick.png",
    day: "/innervoice-screens/en-day.png",
    write: "/innervoice-screens/en-write.png",
    reflect: "/innervoice-screens/en-reflect.png",
  },
  es: {
    pick: "/innervoice-screens/es-pick.png",
    day: "/innervoice-screens/es-day.png",
    write: "/innervoice-screens/es-write.png",
    reflect: "/innervoice-screens/es-reflect.png",
  },
};

function getPreviewScreenshot(lang, key) {
  const bucket = PREVIEW_SCREENSHOTS[lang];
  if (!bucket) return null;
  return bucket[key] || null;
}

function listMissingPreviewAssets(lang) {
  const bucket = PREVIEW_SCREENSHOTS[lang];
  if (!bucket) return PREVIEW_SCREEN_KEYS.map((key) => `${lang}/${key}`);
  return PREVIEW_SCREEN_KEYS.filter((key) => !bucket[key]).map((key) => `${lang}/${key}`);
}

const PREVIEW_MOCK = {
  en: {
    pickRows: ["Anxiety", "Relationship breakup", "Overthinking"],
    dayPill: "Day 3 · 14",
    dayTitle: "Guidance for today",
    writeLabel: "Your words",
    writeGhost: "What I’m noticing…",
    reflectTitle: "What surfaced",
    reflectRows: ["A pattern you named", "One gentle next step"],
  },
  he: {
    pickRows: ["חרדה", "כאב של פרידה טרייה", "מחשבות חוזרות"],
    dayPill: "יום 3 מתוך 14",
    dayTitle: "הדרכה להיום",
    writeLabel: "המילים שלכם",
    writeGhost: "מה שעולה בי כרגע…",
    reflectTitle: "מה קורה בתוככם",
    reflectRows: ["דפוס שזיהיתם", "צעד קטן קדימה"],
  },
  es: {
    pickRows: ["Ansiedad", "Ruptura", "Rumiación"],
    dayPill: "Día 3 · 14",
    dayTitle: "Guía de hoy",
    writeLabel: "Tu escritura",
    writeGhost: "Lo que noto es…",
    reflectTitle: "Lo que apareció",
    reflectRows: ["Un patrón que nombraste", "Un siguiente paso suave"],
  },
};

function PreviewPhone({ title, hint, k, mock, screenshotSrc, lang, assetKey }) {
  const [shotBroken, setShotBroken] = useState(false);
  const useShot = Boolean(screenshotSrc) && !shotBroken;
  const alt = `${title} — ${hint}`.trim();

  return (
    <figure className="ivlp-phone">
      <div className="ivlp-phone-frame">
        <div
          className={
            useShot ? "ivlp-phone-screen ivlp-phone-screen--real" : `ivlp-phone-screen ivlp-phone-screen--${k}`
          }
        >
          {useShot ? (
            <img
              className="ivlp-phone-shot"
              src={screenshotSrc}
              alt={alt}
              loading="lazy"
              decoding="async"
              onError={() => setShotBroken(true)}
            />
          ) : (
            <>
              {screenshotSrc && shotBroken ? (
                <div className="ivlp-preview-asset-missing" role="alert">
                  Preview asset unavailable ({lang}/{assetKey})
                </div>
              ) : null}
              <div className="ivlp-phone-notch" aria-hidden="true" />
              <div className={`ivlp-mock ivlp-mock--${k}`}>
                <div className="ivlp-mock-status" aria-hidden="true">
                  <span className="ivlp-mock-status-time">9:41</span>
                  <span className="ivlp-mock-status-bars">
                    <span />
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
                {k === "pick" ? (
                  <>
                    <div className="ivlp-mock-bar" />
                    <div className="ivlp-mock-pick-list">
                      {mock.pickRows.map((label, i) => (
                        <div key={i} className={`ivlp-mock-pick-row${i === 0 ? " ivlp-mock-pick-row--on" : ""}`}>
                          <span className="ivlp-mock-pick-txt">{label}</span>
                          <span className="ivlp-mock-pick-chev" aria-hidden="true" />
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
                {k === "day" ? (
                  <>
                    <div className="ivlp-mock-day-top">
                      <span className="ivlp-mock-pill">{mock.dayPill}</span>
                      <span className="ivlp-mock-day-title">{mock.dayTitle}</span>
                    </div>
                    <div className="ivlp-mock-day-progress" aria-hidden="true">
                      <span className="ivlp-mock-day-progress-fill" />
                    </div>
                    <div className="ivlp-mock-day-body">
                      <div className="ivlp-mock-block ivlp-mock-block--lead" />
                      <div className="ivlp-mock-block" />
                      <div className="ivlp-mock-block ivlp-mock-block--short" />
                    </div>
                  </>
                ) : null}
                {k === "write" ? (
                  <>
                    <span className="ivlp-mock-write-label">{mock.writeLabel}</span>
                    <div className="ivlp-mock-textarea" role="presentation">
                      <span className="ivlp-mock-write-ghost">{mock.writeGhost}</span>
                      <span className="ivlp-mock-write-line" />
                      <span className="ivlp-mock-write-line" />
                    </div>
                  </>
                ) : null}
                {k === "reflect" ? (
                  <>
                    <span className="ivlp-mock-reflect-h">{mock.reflectTitle}</span>
                    <ul className="ivlp-mock-reflect-list">
                      {mock.reflectRows.map((row, i) => (
                        <li key={i}>
                          <span className="ivlp-mock-dot" aria-hidden="true" />
                          {row}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      <figcaption className="ivlp-phone-cap">
        <strong>{title}</strong>
        <span>{hint}</span>
      </figcaption>
    </figure>
  );
}

const WHAT_ICONS = ["spark", "shield", "path", "pen"];
const WHY_ICONS = ["mind", "heart", "pulse", "anchor"];

function IvPullStrip({ text, className = "" }) {
  if (!text || !String(text).trim()) return null;
  return (
    <div className={`ivlp-pull-strip ${className}`.trim()} role="note">
      <p className="ivlp-pull-text ivlp-preline">{text}</p>
    </div>
  );
}

export default function InnerVoiceLanding() {
  const [lang, setLang] = useState(detectLang);
  const t = useMemo(() => IV_LANDING[lang] || IV_LANDING.en, [lang]);
  const landingReadAloud = useMemo(
    () =>
      collectUiStringsForTts({
        ...t,
        support: SUPPORT_EMAIL,
        previewMock: PREVIEW_MOCK[lang] || PREVIEW_MOCK.en,
      }),
    [lang, t]
  );

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.documentElement.dir = t.dir;
    window.localStorage.setItem("innervoice_landing_lang", lang);
    window.localStorage.setItem("innervoice_lang", lang);
  }, [lang, t]);

  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "smooth";
    return () => {
      document.documentElement.style.scrollBehavior = prev;
    };
  }, []);

  useEffect(() => {
    const missing = listMissingPreviewAssets(lang);
    if (missing.length) {
      console.error(`[InnerVoiceLanding] Missing preview assets: ${missing.join(", ")}`);
    }
  }, [lang]);

  const switchLang = (next) => {
    setLang(next);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next);
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <div className="ivlp" dir={t.dir} data-ivlp-build="app-design-system-v1">
      <header className="ivlp-top">
        <a href="#top" className="ivlp-brand-link" aria-label={t.brand}>
          <InnerVoiceLogoLockup showWordmark={false} className="ivlp-logo-lockup--header" />
        </a>
        <div className="ivlp-lang" role="group" aria-label="Language">
          {["he", "en", "es"].map((code) => (
            <button
              key={code}
              type="button"
              className={lang === code ? "ivlp-lang--on" : ""}
              onClick={() => switchLang(code)}
            >
              {code === "he" ? "עברית" : code === "es" ? "Español" : "English"}
            </button>
          ))}
        </div>
      </header>

      <section className="ivlp-hero" id="top">
        <div className="ivlp-hero-inner">
          <div className="ivlp-hero-mark">
            <InnerVoiceLogoLockup showWordmark className="ivlp-logo-lockup--hero" />
          </div>
          <h1 className="ivlp-hero-title">{t.heroTitle}</h1>
          {t.heroSubtitle?.trim() ? <p className="ivlp-hero-lead">{t.heroSubtitle}</p> : null}
          <div className="ivlp-hero-actions">
            <Link className="ivlp-btn ivlp-btn--large" to={journeyHref(lang)}>
              {t.heroCta}
            </Link>
            <a className="ivlp-btn ivlp-btn--secondary" href="#what">
              {t.heroBadge}
            </a>
          </div>
          {t.heroTrustLine?.trim() ? <p className="ivlp-hint">{t.heroTrustLine}</p> : null}
          {t.heroAfterClick?.trim() ? <p className="ivlp-hint ivlp-hint--after-click">{t.heroAfterClick}</p> : null}
          {t.bookCrosslink?.trim() ? (
            <p className="ivlp-hint ivlp-hint--cross">
              <Link className="ivlp-cross-link" to={withLang("/book", lang)}>
                {t.bookCrosslink}
              </Link>
            </p>
          ) : null}
        </div>
        <ReadAloudBar text={landingReadAloud} lang={lang} className="ivlp-read-aloud" />
      </section>

      <IvPullStrip text={t.pullQuoteAfterHero} />

      <section className="ivlp-section" id="what">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="spark" title={t.whatTitle} />
            <div
              className={`ivlp-card-grid ${
                t.whatCards.length === 3 ? "ivlp-card-grid--3" : "ivlp-card-grid--4"
              }`}
            >
              {t.whatCards.map((c, i) => (
                <article key={`what-${i}-${c.title || "card"}`} className="ivlp-glass">
                  <div className="ivlp-glass-icon">
                    <IvIcon name={WHAT_ICONS[i] || "spark"} />
                  </div>
                  {c.title ? <h3>{c.title}</h3> : null}
                  <p className={`ivlp-preline${c.title ? "" : " ivlp-card-body--no-title"}`}>{c.body}</p>
                </article>
              ))}
            </div>
          </div>
        </IvReveal>
      </section>

      {t.practicalTitle && Array.isArray(t.practicalItems) && t.practicalItems.length > 0 ? (
        <section className="ivlp-section ivlp-section--band" id="practical">
          <IvReveal>
            <div className="ivlp-inner ivlp-inner--wide">
              <SectionHead icon="layers" title={t.practicalTitle} />
              <ul className="ivlp-benefits-list">
                {t.practicalItems.map((line, i) => (
                  <li key={i} className="ivlp-benefit-line ivlp-preline">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </IvReveal>
        </section>
      ) : null}

      <section className="ivlp-section" id="preview">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--max">
            <SectionHead icon="phone" title={t.previewTitle} lead={t.previewLead} />
            <div className="ivlp-preview-row">
              {t.previewScreens.map((s) => (
                <PreviewPhone
                  key={s.key}
                  k={s.key}
                  title={s.title}
                  hint={s.hint}
                  mock={PREVIEW_MOCK[lang] || PREVIEW_MOCK.en}
                  lang={lang}
                  assetKey={s.key}
                  screenshotSrc={getPreviewScreenshot(lang, s.key)}
                />
              ))}
            </div>
          </div>
        </IvReveal>
      </section>

      <IvPullStrip text={t.pullQuoteAfterWhat} />

      <section className="ivlp-section ivlp-section--band" id="why">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="path" title={t.whyTitle} lead={t.whyLead} />
            <div className="ivlp-card-grid ivlp-card-grid--4">
              {t.whyCards.map((c, i) => (
                <article key={`why-${i}-${c.title || "pain"}`} className="ivlp-glass">
                  <div className="ivlp-glass-icon">
                    <IvIcon name={WHY_ICONS[i] || "path"} />
                  </div>
                  {c.title ? <h3>{c.title}</h3> : null}
                  <p className={`ivlp-preline${c.title ? "" : " ivlp-card-body--no-title"}`}>{c.body}</p>
                </article>
              ))}
            </div>
          </div>
        </IvReveal>
      </section>

      <section className="ivlp-section ivlp-section--band" id="not-ai">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="chatOff" title={t.notAiTitle} />
            <div className="ivlp-compare-stack">
              {t.notAiPairs.map((row, i) => (
                <div key={i} className="ivlp-compare-pair">
                  <div className="ivlp-compare-block ivlp-compare-block--other">
                    <span className="ivlp-compare-label">{t.notAiColLeft}</span>
                    <p className="ivlp-compare-text">{row.left}</p>
                  </div>
                  <div className="ivlp-compare-divider" aria-hidden="true" />
                  <div className="ivlp-compare-block ivlp-compare-block--us">
                    <span className="ivlp-compare-label ivlp-compare-label--us">{t.notAiColRight}</span>
                    <p className="ivlp-compare-text ivlp-compare-text--us">{row.right}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="ivlp-notai-close ivlp-preline">{t.notAiClosing}</p>
          </div>
        </IvReveal>
      </section>

      {t.trustTitle && Array.isArray(t.trustItems) && t.trustItems.length > 0 ? (
        <section className="ivlp-section" id="trust">
          <IvReveal>
            <div className="ivlp-inner ivlp-inner--wide">
              <SectionHead icon="shield" title={t.trustTitle} />
              <ul className="ivlp-benefits-list">
                {t.trustItems.map((line, i) => (
                  <li key={i} className="ivlp-benefit-line ivlp-preline">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </IvReveal>
        </section>
      ) : null}

      <IvPullStrip text={t.pullQuoteMid} />

      <section className="ivlp-section" id="how">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="steps" title={t.howTitle} />
            <div className="ivlp-steps-row">
              {t.howSteps.map((step, i) => (
                <article key={step.n} className="ivlp-step-card ivlp-glass">
                  <div className="ivlp-step-card-top">
                    <div className="ivlp-step-num">{step.n}</div>
                    {i < t.howSteps.length - 1 ? <div className="ivlp-step-connector" aria-hidden="true" /> : null}
                  </div>
                  <h3>{step.title}</h3>
                  <p className="ivlp-preline">{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </IvReveal>
      </section>

      {t.benefitsTitle && Array.isArray(t.benefitsLines) && t.benefitsLines.length > 0 ? (
        <section className="ivlp-section ivlp-section--band" id="benefits">
          <IvReveal>
            <div className="ivlp-inner ivlp-inner--wide">
              <SectionHead icon="layers" title={t.benefitsTitle} />
              <ul className="ivlp-benefits-list">
                {t.benefitsLines.map((line, i) => (
                  <li key={i} className="ivlp-benefit-line ivlp-preline">
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </IvReveal>
        </section>
      ) : null}

      <section className="ivlp-section ivlp-section--band" id="journeys">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="map" title={t.journeysTitle} lead={t.journeysIntro} />
            <div className="ivlp-pills">
              {t.journeysList.map((name) => (
                <span key={name} className="ivlp-pill">
                  {name}
                </span>
              ))}
              <span className="ivlp-pill">{t.journeysMore}</span>
            </div>
            <p className="ivlp-j-foot">{t.journeysFoot}</p>
          </div>
        </IvReveal>
      </section>

      <IvPullStrip text={t.pullQuoteBeforeWho} />

      <section className="ivlp-section" id="who">
        <IvReveal>
          <div className="ivlp-inner ivlp-inner--wide">
            <SectionHead icon="people" title={t.whoTitle} lead={t.whoLead} />
            <div className="ivlp-who-grid">
              {t.whoList.map((item) => (
                <div key={item.text} className="ivlp-who-card ivlp-glass">
                  <span className="ivlp-who-ic" aria-hidden="true">
                    <IvIcon name={item.icon} />
                  </span>
                  <span className="ivlp-who-txt">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="ivlp-who-close ivlp-preline">{t.whoClosing}</p>
          </div>
        </IvReveal>
      </section>

      {t.faqTitle && Array.isArray(t.faqItems) && t.faqItems.length > 0 ? (
        <section className="ivlp-section ivlp-section--band" id="faq">
          <IvReveal>
            <div className="ivlp-inner ivlp-inner--wide">
              <SectionHead icon="spark" title={t.faqTitle} />
              <div className="ivlp-faq-list">
                {t.faqItems.map((item, i) => (
                  <details key={i} className="ivlp-faq-item ivlp-glass">
                    <summary>{item.q}</summary>
                    <p className="ivlp-preline">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </IvReveal>
        </section>
      ) : null}

      {t.journeyTagline?.trim() ? (
        <div className="ivlp-band">
          <IvReveal>
            <p className="ivlp-preline">{t.journeyTagline}</p>
          </IvReveal>
        </div>
      ) : null}

      <section className="ivlp-section ivlp-section--cta" id="start">
        <IvReveal>
          <div className="ivlp-inner">
            <div className="ivlp-cta-glass ivlp-glass ivlp-glass--pad-lg">
              <h2>{t.ctaTitle}</h2>
              <p className="ivlp-cta-text ivlp-preline">{t.ctaText}</p>
              <Link className="ivlp-btn ivlp-btn--large" to={journeyHref(lang)}>
                {t.ctaButton}
              </Link>
            </div>
          </div>
        </IvReveal>
      </section>

      <footer className="ivlp-footer">
        <div className="ivlp-footer-grid">
          <div>
            <div className="ivlp-footer-brand">
              <InnerVoiceLogoLockup showWordmark={false} className="ivlp-logo-lockup--footer" />
              <div>
                <div className="ivlp-footer-title">{t.brand}</div>
                <p className="ivlp-footer-tag">{t.footerTagline}</p>
              </div>
            </div>
          </div>
          <nav className="ivlp-footer-links" aria-label="Footer">
            <Link to={withLang("/", lang)}>
              {lang === "he" ? "בית" : lang === "es" ? "Inicio" : "Home"}
            </Link>
            <Link to={withLang("/book", lang)}>
              {lang === "he" ? "הספר" : lang === "es" ? "Libro" : "Book"}
            </Link>
            <a href="/orasis">{lang === "he" ? "Orasis" : "Orasis"}</a>
            <Link to={withLang("/legal/privacy", lang)}>{t.footerPrivacy}</Link>
            <Link to={withLang("/legal/terms", lang)}>{t.footerTerms}</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{t.footerContact}</a>
          </nav>
        </div>
        <p className="ivlp-footer-copy">
          © {new Date().getFullYear()} InnerVoice · Temple of Light
        </p>
      </footer>
    </div>
  );
}
